import { create } from 'zustand';

export type ConsentStatus = 'pending' | 'accepted' | 'rejected';

export interface ConsentPreferences {
    analytics: boolean;
    marketing: boolean;
}

interface ConsentState {
    status: ConsentStatus;
    preferences: ConsentPreferences;
    hasConsented: () => boolean;
    setConsent: (preferences: ConsentPreferences) => void;
    acceptAll: () => void;
    rejectAll: () => void;
    reset: () => void;
}

const STORAGE_KEY = 'profitability-consent';

const readInitialConsent = (): { status: ConsentStatus; preferences: ConsentPreferences } => {
    if (typeof window === 'undefined') {
        return {
            status: 'pending',
            preferences: { analytics: false, marketing: false }
        };
    }

    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            return {
                status: parsed.status || 'pending',
                preferences: parsed.preferences || { analytics: false, marketing: false }
            };
        }
    } catch (error) {
        console.error('[ConsentStore] Failed to read consent from storage:', error);
    }

    return {
        status: 'pending',
        preferences: { analytics: false, marketing: false }
    };
};

const saveConsent = (status: ConsentStatus, preferences: ConsentPreferences): void => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ status, preferences }));
    } catch (error) {
        console.error('[ConsentStore] Failed to save consent to storage:', error);
    }
};

const initial = readInitialConsent();

export const useConsentStore = create<ConsentState>((set, get) => ({
    status: initial.status,
    preferences: initial.preferences,
    hasConsented: () => {
        const state = get();
        return state.status === 'accepted' || state.status === 'rejected';
    },
    setConsent: (preferences) => {
        const status: ConsentStatus = 'accepted';
        set((state) => ({ ...state, status, preferences }));
        saveConsent(status, preferences);
    },
    acceptAll: () => {
        const preferences: ConsentPreferences = { analytics: true, marketing: true };
        const status: ConsentStatus = 'accepted';
        set({ status, preferences });
        saveConsent(status, preferences);
    },
    rejectAll: () => {
        const preferences: ConsentPreferences = { analytics: false, marketing: false };
        const status: ConsentStatus = 'rejected';
        set({ status, preferences });
        saveConsent(status, preferences);
    },
    reset: () => {
        const preferences: ConsentPreferences = { analytics: false, marketing: false };
        const status: ConsentStatus = 'pending';
        set({ status, preferences });
        if (typeof window !== 'undefined') {
            window.localStorage.removeItem(STORAGE_KEY);
        }
    }
}));

